import { useDispatch, useSelector } from "react-redux";
import { getGateways, getSensors, getSiteAnalytics } from "../../Api Methods/Api";
import { useEffect, useState, useRef, useMemo } from "react";
import statClasses from "../Dashboard/Dashboard.module.css";
import classes from "./gateways.module.css";
import { Await, useNavigate } from "react-router-dom";
import { Line } from "react-chartjs-2";
import PadlocksList from "../Padlocks/padlocksList";
import DeviceLoadingGrid from "../../components/GridList/DeviceLoadingGrid";
import StatCard from "../../components/UI/Stats/StatCard";
import mixpanel from "mixpanel-browser";
import refreshBtn from '../../assets/portalAssets/refreshV2.png';

const LineCard = ({ title, batteryData, signalData, onLegendClick }) => {
    const hasMultipleDatasets = batteryData?.datasets?.length > 1;
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: hasMultipleDatasets,
                onClick: onLegendClick || undefined,
                onHover: onLegendClick ? (e) => { e.native.target.style.cursor = 'pointer'; } : undefined,
                onLeave: onLegendClick ? (e) => { e.native.target.style.cursor = 'default'; } : undefined,
            },
            tooltip: { enabled: true }
        },
        scales: {
            x: { grid: { display: false } },
            y: { beginAtZero: true, max: 100, ticks: { stepSize: 20 } }
        }
    };
    const hasData = batteryData?.datasets?.length > 0;
    return (
        <>
        {!hasData ? (
            <p style={{ textAlign: "center", marginTop: "8rem", color: "#888888" }}>
                No analytics data available for the last 14 days.
            </p>
        ) : (
            <>
            <div className={statClasses.chartCard}>
                <p className={statClasses.chartTitle}>{title}</p>
                <div className={statClasses.barContainer}>
                    <Line data={batteryData} options={options} />
                </div>
            </div>
            <br />
            <div className={statClasses.chartCard}>
                <p className={statClasses.chartTitle}>Signal Strength</p>
                <div className={statClasses.barContainer}>
                    <Line data={signalData} options={options} />
                </div>
            </div>
            </>
        )}
        </>
    );
};

const RTSectionHeaderGateway = ({ title, onRefresh }) => (
    <div className={statClasses.sectionHeaderTime} style={{ marginTop: "12px", marginBottom: "12px"}}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", justifyContent: "space-between", width: "100%" }}>
            <p className={statClasses.sectionTitle}>{title}</p>
            <img className={classes.refreshButton} onClick={onRefresh} src={refreshBtn} />
        </div>
    </div>
);

const SkeletonBarChart = () => (
    <div className={statClasses.chartCard}>
        <div className={statClasses.skeletonBar} style={{ height: "14px", width: "40%", marginBottom: "16px" }} />
        <div className={statClasses.skeletonBar} style={{ height: "170px", width: "100%" }} />
    </div>
);

const Gateways = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const client = useSelector(state => state.auth.client);
    const token = useSelector(state => state.auth.token);
    const companyName = useSelector(state => state.auth.companyName);
    const apiDataRef = useRef({ token, client, companyName });
    apiDataRef.current = { token, client, companyName };

    const role = useSelector(state => state.auth.role);
    const apiData = {
        token,
        client
    }

    const [gateways, setGateways] = useState([])
    const [billableGateways, setBillableGateways] = useState(0)
    const [nonBillableGateways, setNonBillableGateways] = useState(0)
    const [criticalGateways, setCriticalGateways] = useState([])
    const [warningGateways, setWarningGateways] = useState([])
    const [healthyGateways, setHealthyGateways] = useState([]) 
    const [offlineGateways, setOfflineGateways] = useState([])
    const [staleGateways, setStaleGateways] = useState([])
    const [update, setUpdate] = useState(0)
    const filtering = ""
    const [isLoading, setIsLoading] = useState(true)
    const [counts, setCounts] = useState([])
    const [gatewayStats, setGatewayStats] = useState(null)
    const [activeTab, setActiveTab] = useState(0);
    const [isLoadingScheduleStats, setIsLoadingScheduleStats] = useState(true)
    
    const lastRefreshTime = useRef(0);
    const cooldown = 8000; 

    const now = new Date();    
    const last14 = new Date();
    last14.setDate(now.getDate() - 14);

    const parseBatterySignal = (batterySignal) => {
        if (!batterySignal) return { battery: null, signal: null };

        const batteryMatch = batterySignal.match(/battery:\s*(\d+)%/i);
        const signalMatch = batterySignal.match(/signal:\s*(\d+)%/i);

        return {
            battery: batteryMatch ? parseInt(batteryMatch[1], 10) : null,
            signal: signalMatch ? parseInt(signalMatch[1], 10) : null
        };
    };

    // get last reading per day per IMEI with parsed battery/signal
    const processGatewayData = (rawData) => {
        if (!rawData || rawData.length === 0) return [];

        // group by imei & date
        const imeiDateMap = {};

        rawData.forEach(item => {
            const date = item.event_date;
            const imei = item.IMEI || 'unknown';
            const key = `${imei}-${date}`;
            const parsed = parseBatterySignal(item.batterySignal);

            imeiDateMap[key] = {
                event_date: date,
                IMEI: imei,
                battery: parsed.battery,
                signal: parsed.signal,
                batterySignal: item.batterySignal
            };
        });

        return Object.values(imeiDateMap).sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
    };

    // 14 days
    const processedGatewayData = useMemo(() => {
        if (!gatewayStats || gatewayStats.length === 0) return [];
        return gatewayStats;
    }, [gatewayStats]);

    // for 3 months - gateway //// for when im using time periods
    // const processedGatewayData = useMemo(() => {
    // if (!gatewayStats || gatewayStats.length === 0) return [];
    // //if (selectedGateTime !== "3M") return gatewayStats;
    //
    // const map = {};
    //
    // gatewayStats.forEach(item => {
    //     const date = new Date(item.event_date);
    //     const month = date.getMonth();
    //     const imei = item.IMEI || 'unknown';
    //     const key = `${imei}-${month}`;
    //
    //     map[key] = { ...item };
    // });
    //
    // return Object.values(map).sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
    // }, [gatewayStats]);

    const formatEventDate = (dateStr, period) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;

        switch (period) {
        case '7D':
            return date.toLocaleDateString('en-US', { weekday: 'short' });
        case '1M':
            return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        case '3M':
            return date.toLocaleDateString('en-US', { month: 'short' });
        default:
            return dateStr;
        }
    };

    const getGatewayChartData = () => {
        if (!processedGatewayData || processedGatewayData.length === 0) return { batteryData: { labels: [], datasets: [] }, signalData: { labels: [], datasets: [] } };

        const uniqueDates = [...new Set(processedGatewayData.map(item => item.event_date))].sort((a, b) => new Date(a) - new Date(b));
        const uniqueIMEIs = [...new Set(processedGatewayData.filter(item => item.IMEI).map(item => item.IMEI))];
        const formattedLabels = uniqueDates.map(date => formatEventDate(date));

        const batteryDatasets = uniqueIMEIs.map((imei, index) => {
            const hue = (index * 360 / Math.max(uniqueIMEIs.length, 1)) % 360;
            const color = `hsl(${hue}, 70%, 55%)`;

            const data = uniqueDates.map(date => {
                const entry = processedGatewayData.find(item => item.event_date === date && item.IMEI === imei);
                return entry?.battery ?? null;
            });

            return {
                label: imei,
                data,
                borderColor: color,
                backgroundColor: `hsla(${hue}, 70%, 55%, 0.1)`,
                pointRadius: 4,
                pointHoverRadius: 6,
                fill: false,
                tension: 0.3,
                spanGaps: true,
            };
        });

        const signalDatasets = uniqueIMEIs.map((imei, index) => {
            const hue = (index * 360 / Math.max(uniqueIMEIs.length, 1)) % 360;
            const color = `hsl(${hue}, 70%, 45%)`;

            const data = uniqueDates.map(date => {
                const entry = processedGatewayData.find(item => item.event_date === date && item.IMEI === imei);
                return entry?.signal ?? null;
            });

            return {
                label: imei,
                data,
                borderColor: color,
                backgroundColor: `hsla(${hue}, 70%, 45%, 0.1)`,
                pointRadius: 4,
                pointHoverRadius: 6,
                fill: false,
                tension: 0.3,
                spanGaps: true,
            };
        });

        return {
            batteryData: { labels: formattedLabels, datasets: batteryDatasets },
            signalData: { labels: formattedLabels, datasets: signalDatasets }
        };
    };

    const handleGatewayLegendClick = (e, legendItem, legend) => {
        const imei = legendItem.text;
        navigate(`/Dashboard/Gateways/Gateway/${imei}`);
    };

    const { batteryData: batteryChartData, signalData: signalChartData } = getGatewayChartData();

    {/* 
    const handleRefreshStats = async () => {
        const currentTime = Date.now();
        if (currentTime - lastRefreshTime.current < cooldown) {
            setIsLoadingScheduleStats(true);
            setTimeout(() => {
                setIsLoadingScheduleStats(false);
            }, 2000);
            return;
        }
        lastRefreshTime.current = currentTime;

        setIsLoadingScheduleStats(true);
        setGatewayStats([]);
        const response = await getSiteAnalytics(apiDataRef.current, {
            siteUUID: "",
            groupName: apiDataRef.current.companyName || localStorage.getItem("companyName"),
            start_date: last14,
            analyticsType: "gateways"
        }, dispatch);
        const processedGateData = processGatewayData(response.data[0]);
        setGatewayStats(processedGateData);
        setIsLoadingScheduleStats(false);
    };
    */}

    //Pull data on mount
    useEffect(() => {
        const HandleGetGateways = async () => {
            const response = await getGateways(apiData, dispatch);
            console.log(response.data[0]);
            // console.log(JSON.parse(response.data[0][0].result));
            // const parsed = JSON.parse(response.data[0][0].result);
            // setGateways(parsed.data);
            // setCounts(parsed.counts)
            // console.log(counts);
            setBillableGateways(response.data[0].filter(lock => lock.billable == 1 && lock.billedToName === companyName))
            setNonBillableGateways(response.data[0].filter(lock => lock.billable == 0))
            setCriticalGateways(response.data[0].filter(lock => lock.battery <= 10))
            setWarningGateways(response.data[0].filter(lock => lock.battery > 10 && lock.battery < 30))
            setHealthyGateways(response.data[0].filter(lock => lock.battery >= 30))
            setOfflineGateways(response.data[0].filter(lock => lock.status == "stale"))
            setStaleGateways(response.data[0].filter(lock => lock.status == "potentiallyStale"))
            setGateways(response.data[0])
            setIsLoading(false)
        }

        {/*
        const HandleGetGatewayStats = async () => {
            const response = await getSiteAnalytics(apiDataRef.current, {
                siteUUID: "",
                groupName: apiDataRef.current.companyName || localStorage.getItem("companyName"),
                start_date: last14,
                analyticsType: "gateways"
            }, dispatch);
            const processedGateData = processGatewayData(response.data[0]);
            setGatewayStats(processedGateData);
            setIsLoadingScheduleStats(false);
        } 
            */}
        
        HandleGetGateways();
        // HandleGetGatewayStats();
   
        mixpanel.track("Gateways tab visited", {
            user: localStorage.getItem('email')
        });
    }, [update])

    /*
    useEffect(() => {
        const currentRequestId = ++gatewayRequestId.current;
        setGatewayStats([]);
        setIsLoadingScheduleStats(true);
        const FetchGatewayStats = async () => {
            const response = await getSiteAnalytics(apiDataRef.current, {
                siteUUID: "",
                groupName: apiDataRef.current.companyName || localStorage.getItem("companyName"),
                start_date: last14,
                analyticsType: "gateways"
            }, dispatch);
            if (currentRequestId !== gatewayRequestId.current) return;
            const processedGateData = processGatewayData(response.data[0]);
            setGatewayStats(processedGateData);
            setIsLoadingScheduleStats(false);
        } 
        FetchGatewayStats();
    }) */


    return (
        <Await resolve={gateways}>
                <div style={{ width: "100%" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 290px))", gap: "var(--gap-lg)", marginBottom: "8px", justifyContent: "start" }}>
                        <StatCard title={"Gateways"} value={gateways.length} isLoading={isLoading} />
                        <StatCard title={"Offline gateways"} value={offlineGateways.length} isLoading={isLoading} />
                        <StatCard title={"Check-in missed"} value={staleGateways.length} isLoading={isLoading} />
                        <StatCard title={"Critical gateways (<10% battery)"} value={criticalGateways.length} isLoading={isLoading} />
                        <StatCard title={"Warning gateways (<30% battery)"} value={warningGateways.length} isLoading={isLoading} />
                        <StatCard title={"Healthy gateways (>30% battery)"} value={healthyGateways.length} isLoading={isLoading} />
                        <StatCard title={"Billable gateways"} value={billableGateways.length} isLoading={isLoading} />
                        <StatCard title={"Non-billable gateways"} value={nonBillableGateways.length} isLoading={isLoading} />
                    </div>
                   
                    <br></br>

                    <div className={classes.tabsWrapper}>
                        <div className={classes.tabs}>
                            {["Gateways"].map((tab, i) => (
                                <button
                                    key={tab}
                                    className={`${classes.tab} ${activeTab === i ? classes.active : ""}`}
                                    onClick={() => setActiveTab(i)}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        <div className={classes.tabContent}>
                            {activeTab === 0 && (
                                isLoading ? (
                                    <div style={{ display: "flex", width: "100%" }}>
                                        <DeviceLoadingGrid CardCount={20} />
                                    </div>
                                ) : (
                                    <PadlocksList
                                        locks={gateways}
                                        filtering={filtering}
                                        update={update}
                                        setUpdate={setUpdate}
                                        isGateways={true}
                                    />
                                )
                            )}
                            {/*
                            {activeTab === 1 && (
                                <div style={{ width: "100%" }}>
                                    <RTSectionHeaderGateway title="Gateways health - Last 14 Days" onRefresh={handleRefreshStats} />
                                    <div className={statClasses.fullbarWrapper}>
                                        {isLoadingScheduleStats ? <SkeletonBarChart /> : (
                                            <LineCard
                                                title="Battery Levels"
                                                batteryData={batteryChartData}
                                                signalData={signalChartData}
                                                onLegendClick={handleGatewayLegendClick}
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                                */}
                        </div>
                    </div>
                </div>
           
        </Await>
    );
}


export default Gateways;